import React, { useState, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    TextField,
    Alert,
    CircularProgress,
    IconButton,
    Chip,
    Paper,
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Close as CloseIcon,
    AutoAwesome as AiIcon,
    InsertDriveFile as FileIcon,
    PictureAsPdf as PdfIcon,
    TableChart as CsvIcon,
    DataObject as JsonIcon,
} from '@mui/icons-material';
import { questionnaireService, IGeneratedQuestionnaireDraft } from '../services/questionnaireService';
import { stitchColors } from '../theme/tokens';

interface GenerateQuestionnaireModalProps {
    open: boolean;
    onClose: () => void;
    onGenerated: (draft: IGeneratedQuestionnaireDraft) => void;
    isAdmin?: boolean;
}

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export const GenerateQuestionnaireModal: React.FC<GenerateQuestionnaireModalProps> = ({
    open,
    onClose,
    onGenerated,
    isAdmin = false,
}) => {
    const [file, setFile] = useState<File | null>(null);
    const [instruction, setInstruction] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleReset = () => {
        setFile(null);
        setInstruction('');
        setError(null);
        setLoading(false);
        setIsDragging(false);
    };

    const handleClose = () => {
        if (!loading) {
            handleReset();
            onClose();
        }
    };

    const validateAndSetFile = (selectedFile: File) => {
        setError(null);
        const name = selectedFile.name.toLowerCase();
        const isPdf = name.endsWith('.pdf');
        const isCsv = name.endsWith('.csv');
        const isJson = name.endsWith('.json') || name.endsWith('.txt');

        if (!isPdf && !isCsv && !isJson) {
            setError('Please select a valid PDF, CSV, or JSON file.');
            return;
        }

        if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
            setError(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit.`);
            return;
        }

        setFile(selectedFile);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            validateAndSetFile(e.target.files[0]);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            validateAndSetFile(e.dataTransfer.files[0]);
        }
    };

    const handleSubmit = async () => {
        if (!file) {
            setError('Please select a document file to upload.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const draft = isAdmin
                ? await questionnaireService.generateWelcomeQuestionnaireFromFile(file, instruction.trim() || undefined)
                : await questionnaireService.generateFromFile(file, instruction.trim() || undefined);

            onGenerated(draft);
            handleClose();
        } catch (err: any) {
            console.error('Failed to generate questionnaire from file:', err);
            const serverMessage = err.response?.data?.error || err.response?.data?.message || err.message;
            setError(serverMessage || 'Failed to generate questionnaire. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const getFileIcon = () => {
        if (!file) return <FileIcon color="action" />;
        const name = file.name.toLowerCase();
        if (name.endsWith('.pdf')) return <PdfIcon sx={{ color: '#d32f2f' }} />;
        if (name.endsWith('.csv')) return <CsvIcon sx={{ color: '#2e7d32' }} />;
        if (name.endsWith('.json')) return <JsonIcon sx={{ color: '#ed6c02' }} />;
        return <FileIcon color="action" />;
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3, p: 1 }
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            bgcolor: stitchColors.surfaceContainer,
                            color: stitchColors.primary
                        }}
                    >
                        <AiIcon fontSize="small" />
                    </Box>
                    <Typography variant="h6" fontWeight={600}>
                        Generate Questionnaire from File
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} disabled={loading} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 2.5 }}>
                <Typography variant="body2" color="text.secondary">
                    Upload an existing intake form, questionnaire PDF, CSV list, or JSON schema. AI will extract questions, field types, and options into structured questionnaire steps.
                </Typography>

                {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                {/* Upload Zone */}
                {!file ? (
                    <Box
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        sx={{
                            border: `2px dashed ${isDragging ? stitchColors.primary : stitchColors.outlineVariant}`,
                            borderRadius: 2,
                            p: 3,
                            textAlign: 'center',
                            cursor: 'pointer',
                            bgcolor: isDragging ? stitchColors.surfaceContainer : stitchColors.surfaceContainerLowest,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                borderColor: stitchColors.primary,
                                bgcolor: stitchColors.surfaceContainerLow,
                            },
                        }}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            data-testid="file-upload-input"
                            accept=".pdf,.csv,.json,application/pdf,text/csv,application/json"
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                        />
                        <UploadIcon sx={{ fontSize: 44, color: isDragging ? stitchColors.primary : 'text.secondary', mb: 1 }} />
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            Click to upload or drag and drop
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Supports PDF, CSV, or JSON (max {MAX_FILE_SIZE_MB}MB)
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
                            <Chip size="small" label="PDF Forms" variant="outlined" />
                            <Chip size="small" label="CSV Spreadsheets" variant="outlined" />
                            <Chip size="small" label="JSON Schemas" variant="outlined" />
                        </Box>
                    </Box>
                ) : (
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            bgcolor: stitchColors.surfaceContainerLowest,
                            borderRadius: 2,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
                            {getFileIcon()}
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="body2" fontWeight={600} noWrap>
                                    {file.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {formatFileSize(file.size)}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton
                            size="small"
                            onClick={() => setFile(null)}
                            disabled={loading}
                            color="error"
                            title="Remove file"
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Paper>
                )}

                {/* Additional Instructions */}
                <TextField
                    label="Custom Instructions / Prompt (Optional)"
                    placeholder="e.g. Focus on estate planning, family details, and real estate assets..."
                    value={instruction}
                    onChange={(e) => setInstruction(e.target.value)}
                    multiline
                    rows={2}
                    size="small"
                    fullWidth
                    disabled={loading}
                />

                {loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: stitchColors.surfaceContainerLow, borderRadius: 2 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">
                            Analyzing document structure and generating questionnaire steps with AI...
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose} disabled={loading} color="inherit">
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!file || loading}
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AiIcon />}
                    sx={{ bgcolor: stitchColors.primary, '&:hover': { bgcolor: stitchColors.primaryContainer } }}
                >
                    {loading ? 'Generating...' : 'Generate Questionnaire'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GenerateQuestionnaireModal;
